// Правила «Тропинки» и упражнений «Грамматики» (2026-10-23) — без импортов приложения: их проверяет
// tests/grammar-path.test.ts под Node.

export interface PathProgress {
  opened: boolean;
  /** Тема пройдена — сделано упражнение №1 (сервер: `completed_at` у строки № 1). */
  passed?: boolean;
  /** Урок дочитан до последнего шага (grammar-completion-tz § 8, B2); старый сервер поля не отдаёт. */
  lesson_done?: boolean;
  exercises_total: number;
  exercises_done: number;
}

/** Последний открытый урок — для «Продолжить» на главной (пишет LessonPlayer). */
export const GRAMMAR_LAST_KEY = 'lx.grammar.last';

/** Отметка «урок прочитан» в этом браузере — запасной путь, пока сервер не отдаёт `progress.lesson_done`. */
export const grammarReadKey = (slug: string) => `lx.grammar.read.${slug}`;

export interface RewardInfo {
  topics_step: number;
  passed: number;
  next_at: number;
}

/** Круг наград: сколько тем текущего круга пройдено (`done`) и сколько осталось до награды (`left`). */
export function rewardCycle(rewards: RewardInfo): { step: number; done: number; left: number } {
  const step = Math.max(1, rewards.topics_step);
  const done = Math.min(step, Math.max(0, rewards.passed - (rewards.next_at - step)));
  return { step, done, left: step - done };
}

export interface PathTopic {
  slug: string;
  level: string;
  order: number;
  progress: PathProgress;
}

/**
 * Тема пройдена — сделано упражнение 1 (`progress.passed`). Одно правило для галочки на тропинке, счётчика уровня,
 * «ты здесь», «Продолжить» на тропинке и на главной, награды и итогов упражнения (владелец 2026-10-26: «непонятно
 * как… надо все ли упражнения открыть»). Упражнения 2–4 статус темы не меняют.
 */
export function isTopicDone(progress: Pick<PathProgress, 'passed'>): boolean {
  return progress.passed === true;
}

export type TopicStatus = 'open' | 'passed' | 'mastered';

/** Статус темы для ученика: не пройдена → пройдена (упражнение 1) → освоена («Сложное» и чистый повтор через неделю). */
export function topicStatus(topic: { progress: Pick<PathProgress, 'passed'>; mastered?: boolean }): TopicStatus {
  if (topic.mastered) return 'mastered';
  return isTopicDone(topic.progress) ? 'passed' : 'open';
}

/**
 * Тема для «Продолжить» — и на главной, и «ты здесь» на тропинке (одна функция, 2026-10-26). Темы проходят в любом
 * порядке, поэтому сначала — последний открытый урок, если он ещё не пройден; иначе первая непройденная тема пути.
 * Пройденная тема «ты здесь» не бывает; пройдено всё — `null`.
 */
export function homeTopic<T extends PathTopic>(topics: readonly T[], lastSlug: string | null): T | null {
  const last = lastSlug ? topics.find((t) => t.slug === lastSlug && !isTopicDone(t.progress)) : undefined;
  return last ?? sortPath(topics).find((t) => !isTopicDone(t.progress)) ?? null;
}

/**
 * Куда ведёт «Продолжить» (тропинка и главная): урок дочитан (`progress.lesson_done` или отметка этого браузера), а тема
 * не пройдена — сразу в упражнение 1; иначе — в урок.
 */
export function continueTarget(topic: Pick<PathTopic, 'progress'>, readHere: boolean): 'exercise' | 'lesson' {
  if (isTopicDone(topic.progress)) return 'lesson';
  return topic.progress.lesson_done === true || readHere ? 'exercise' : 'lesson';
}

/** Сегодня по местному календарю (YYYY-MM-DD) — для «пора повторить» у темы. */
export function localDayIso(date: Date = new Date()): string {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
}

/** «Пора повторить» — метка, не статус: тема пройдена, не освоена, и срок повтора наступил (`review_due ≤ сегодня`). */
export function reviewDueNow(
  topic: { progress: Pick<PathProgress, 'passed'>; mastered?: boolean; review_due?: string | null },
  today: string,
): boolean {
  return isTopicDone(topic.progress) && !topic.mastered && !!topic.review_due && topic.review_due <= today;
}

/**
 * Вкладки уровней: только уровни, где есть темы, которые видит ученик (не черновики). Черновики показывает лишь админ
 * (локально — всем), и уровень из одних черновиков вкладкой не показываем (приёмка 2026-10-26: на 320 вкладки шли в два
 * ряда из-за B2). Готовых тем нет нигде — уровни с любыми темами.
 */
export function pathLevels<L extends { id: string }>(levels: readonly L[], topics: readonly { level: string; status?: string }[]): L[] {
  const learner = levels.filter((l) => topics.some((t) => t.level === l.id && t.status !== 'draft'));
  return learner.length ? learner : levels.filter((l) => topics.some((t) => t.level === l.id));
}

/** Счётчик вкладки уровня («A1 2/28»): пройдено из тем, которые видит ученик; на уровне одни черновики — из всех. */
export function levelProgress(topics: readonly (PathTopic & { status?: string })[], level: string): { done: number; total: number } {
  const inLevel = topics.filter((t) => t.level === level);
  const learner = inLevel.filter((t) => t.status !== 'draft');
  const counted = learner.length ? learner : inLevel;
  return { done: counted.filter((t) => isTopicDone(t.progress)).length, total: counted.length };
}

/** Отрезки дорожки между соседними шагами: зелёный — обе темы пройдены (темы проходят в любом порядке). */
export function trailDone(topics: readonly { progress: Pick<PathProgress, 'passed'> }[]): boolean[] {
  return topics.slice(1).map((topic, i) => isTopicDone(topics[i].progress) && isTopicDone(topic.progress));
}

/** Сложность упражнения по его номеру — как на сервере по умолчанию (`EXERCISE_LEVELS[no − 1]`). */
export const EXERCISE_LEVELS = ['easy', 'medium', 'hard', 'master'] as const;
export type ExerciseLevelKey = (typeof EXERCISE_LEVELS)[number];

/**
 * Правило «освоена» для легенды и плашки урока: упражнение и дни из `GET /topics → mastery` (§ 8, B3), чтобы тексты не
 * разошлись с настройкой сервера. Нет поля (старый сервер) или оно битое — «Сложное» (№ 3) и 7 дней.
 */
export function masteryRule(mastery?: { exercise_no?: number; days?: number } | null): { level: ExerciseLevelKey; days: number } {
  const no = mastery?.exercise_no;
  const level = (typeof no === 'number' && Number.isInteger(no) ? EXERCISE_LEVELS[no - 1] : undefined) ?? 'hard';
  const days = typeof mastery?.days === 'number' && Number.isInteger(mastery.days) && mastery.days > 0 ? mastery.days : 7;
  return { level, days };
}

export type FinishAction = 'nextExercise' | 'openExercise' | 'nextTopic' | 'toLesson';

/**
 * Кнопки итогов упражнения, когда тема пройдена (владелец 2026-10-26, вариант А: платное — не по умолчанию).
 * Следующее упражнение открыто — «Дальше: упражнение N», вторая «Следующая тема»; закрыто — «Следующая тема», вторая
 * «Открыть упражнение N · 1 жетон»; следующего упражнения нет — «Следующая тема»; следующей темы нет — «К уроку».
 * `lessonLink` — «К уроку» в ряду текстовых кнопок (не дублируется, если уже главная).
 */
export function finishActions(
  next: { open: boolean } | null,
  hasNextTopic: boolean,
): { primary: FinishAction; secondary: FinishAction | null; lessonLink: boolean } {
  if (next?.open) return { primary: 'nextExercise', secondary: hasNextTopic ? 'nextTopic' : null, lessonLink: true };
  const primary: FinishAction = hasNextTopic ? 'nextTopic' : 'toLesson';
  return { primary, secondary: next ? 'openExercise' : null, lessonLink: primary !== 'toLesson' };
}

const LEVELS = ['A1', 'A2', 'B1', 'B2', 'C1', 'C2'];

/** Порядок тропинки: по уровню, внутри уровня — по `order`. */
export function sortPath<T extends PathTopic>(topics: readonly T[]): T[] {
  const rank = (level: string) => {
    const i = LEVELS.indexOf(level);
    return i < 0 ? LEVELS.length : i;
  };
  return [...topics].sort((a, b) => rank(a.level) - rank(b.level) || a.order - b.order);
}

/** Сдвиг шага тропинки вправо, px: мягкий зигзаг 0 → 32 → 64 → 32 → 0… */
export const PATH_STEP_OFFSETS = [0, 32, 64, 32] as const;

export function pathOffset(index: number): number {
  return PATH_STEP_OFFSETS[index % PATH_STEP_OFFSETS.length];
}

/** Звёзды за упражнение: всё верно — 3, хотя бы 60 % — 2, хоть что-то — 1. */
export function exerciseStars(solved: number, total: number): 0 | 1 | 2 | 3 {
  if (total <= 0 || solved <= 0) return 0;
  const share = solved / total;
  if (share >= 1) return 3;
  if (share >= 0.6) return 2;
  return 1;
}

export interface PlayItemState {
  state: { solved: boolean } | null;
}

/** С какого задания начать: первое нерешённое; решено всё — с начала (повтор). */
export function firstPendingItem(items: readonly PlayItemState[]): number {
  const i = items.findIndex((item) => !item.state?.solved);
  return i < 0 ? 0 : i;
}
